/**
 * Typed ride-execution endpoint group for the Vello real clients (S101+; M12). Wraps the S093 `vello-api`
 * execution surface. S101 needs only the **timeline** read — the durable source a client re-reads on relaunch
 * to rebuild the authoritative ride state (no impossible screen). S102/S103 extend this group with commands,
 * chat, masked contact, and PIN. The timeline is the durable phase/status + the ride event log; realtime
 * (S101 `RealtimeClient`) carries the live deltas between reads.
 */
import { parseMoney } from '@vello/core';
/**
 * The `pin` command's refusals (D399): `403` with `reasonCode: RIDE_PIN_MISMATCH` and `params: { attemptsLeft }`
 * for a counted wrong PIN (D577; the glued `detail: RIDE_PIN_MISMATCH:<left>` is deprecated); `403 RIDE_PIN_LOCKED` once the budget is spent (the right PIN included); `409
 * RIDE_PIN_UNAVAILABLE` when the ride holds no sealed PIN, and `400 RIDE_PIN_REQUIRED` for a blank PIN (neither is counted). D722: `409 RIDE_PIN_NOT_REQUIRED` for a ride
 * that started with the boarding PIN off (nothing counted).
 */
export const RIDE_PIN_REFUSAL_REASON_CODES = ['RIDE_PIN_MISMATCH', 'RIDE_PIN_LOCKED', 'RIDE_PIN_UNAVAILABLE', 'RIDE_PIN_REQUIRED', 'RIDE_PIN_NOT_REQUIRED'];
/** D826: the refusals of a voice message (besides `RIDE_CHAT_CLOSED`, `RIDE_CHAT_RATE_LIMITED`, `RIDE_CHAT_RECIPIENT_INVALID`). */
export const RIDE_CHAT_AUDIO_REFUSAL_REASON_CODES = ['RIDE_CHAT_AUDIO_DISABLED', 'RIDE_CHAT_AUDIO_INVALID', 'RIDE_CHAT_AUDIO_TOO_LONG', 'RIDE_CHAT_AUDIO_TOO_LARGE', 'RIDE_CHAT_AUDIO_NOT_FOUND', 'RIDE_CHAT_AUDIO_POLICY_UNAVAILABLE'];
/**
 * The chat refusals (D417): `400 RIDE_CHAT_BODY_REQUIRED` (blank), `400 RIDE_CHAT_BODY_TOO_LONG` (over `maxBodyLength`),
 * `409 RIDE_CHAT_CLOSED` (the ride ended), `429 RIDE_CHAT_RATE_LIMITED` (the sender's burst; `retryAfterSeconds` set).
 */
export const RIDE_CHAT_REFUSAL_REASON_CODES = ['RIDE_CHAT_BODY_REQUIRED', 'RIDE_CHAT_BODY_TOO_LONG', 'RIDE_CHAT_CLOSED', 'RIDE_CHAT_RATE_LIMITED', 'RIDE_CHAT_RECIPIENT_INVALID', 'RIDE_CHAT_QUICK_REPLY_UNKNOWN', 'RIDE_CHAT_BODY_AND_REPLY'];
/** Owner decision D408 #4: the call is hidden until a masking provider exists — the reason the contact carries. */
export const RIDE_CONTACT_CALL_UNAVAILABLE = 'CONTACT_CALL_UNAVAILABLE_NO_MASKING_PROVIDER';
export class RideExecutionApi {
    client;
    constructor(client) {
        this.client = client;
    }
    timeline(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/timeline`, auth: true });
    }
    /** The winning driver initializes the execution (S093). Idempotent; returns the initial phase. */
    initExecution(rideId) {
        return this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/execution`, body: {}, auth: true });
    }
    /** Issue a driver execution command (en_route/arrive/pin/start/stop/destination/complete/no_show/cancel). */
    command(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/commands`, body: input, auth: true });
    }
    /** Change the destination mid-trip (S109). Idempotent by commandId; the ride must be in progress. */
    changeDestination(rideId, commandId, newDestination) {
        return this.command(rideId, { commandId, type: 'destination', newDestination });
    }
    /**
     * D689 (gap 3): the assigned driver reads the destination the rider changed mid-trip and confirms it (`ack_destination`). `409 NO_DESTINATION_CHANGE_PENDING` when
     * none waits. Idempotent by commandId.
     */
    acknowledgeDestination(rideId, commandId) {
        return this.command(rideId, { commandId, type: 'ack_destination' });
    }
    /**
     * D689 (gap 3): the RIDER asks what the trip would cost with another destination - a fresh quote of the whole trip (pickup, the ride's stops, the new destination)
     * next to what the ride costs now. Refusals (409, `reasonCode`): `DESTINATION_CHANGE_NOT_ON_BOARD`, `..._NOT_ALLOWED_AGREED_PRICE`, `..._ACCOUNT_BILLED`, `..._DISCOUNT`,
     * `..._INTERCITY`, `DESTINATION_CHANGE_LIMIT_REACHED`; the quote route's own refusals (service area, route) apply too.
     */
    async quoteDestinationChange(rideId, destination) {
        const res = await this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/destination-change/quote`, body: { destination }, auth: true });
        if (!res.ok)
            return res;
        const w = res.value;
        return {
            ok: true,
            value: {
                rideId: w.rideId,
                quoteId: w.quote.quoteId,
                ...(w.quote.expiresAt !== undefined ? { expiresAt: w.quote.expiresAt } : {}),
                total: parseMoney(w.quote.total),
                current: parseMoney(w.current),
                difference: { amountMinor: BigInt(w.difference.amountMinor), currency: w.difference.currency, direction: w.difference.direction },
                destination: w.destination,
                ...(w.fare !== undefined
                    ? {
                        fare: {
                            basis: w.fare.basis, reasonCode: w.fare.reasonCode, policyVersion: w.fare.policyVersion, quoteTotal: parseMoney(w.fare.quoteTotal),
                            ...(w.fare.floor !== undefined ? { floor: parseMoney(w.fare.floor) } : {}),
                            floorApplied: w.fare.floorApplied,
                            ...(w.fare.executed !== undefined ? { executed: { distanceMeters: w.fare.executed.distanceMeters, durationSeconds: w.fare.executed.durationSeconds } } : {}),
                        },
                    }
                    : {}),
            },
        };
    }
    /**
     * D689 (gap 3): the rider applies the change with the quote he saw. Idempotent by `commandId`. The ride's destination and fare follow, and the assigned driver is
     * asked to acknowledge. Refusals: the quote's (`QUOTE_EXPIRED`, `QUOTE_NOT_OWNED`, `REQUEST_STOPS_DO_NOT_MATCH_QUOTE`, `QUOTE_STOPS_MISMATCH`) and the quote step's.
     */
    async applyDestinationChange(rideId, input) {
        const res = await this.client.request({
            method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/destination-change`, body: input, auth: true,
        });
        if (!res.ok)
            return res;
        const w = res.value;
        return { ok: true, value: { rideId: w.rideId, phase: w.phase, status: w.status, version: w.version, destination: w.destination, previousTotal: parseMoney(w.fare.previous), total: parseMoney(w.fare.total) } };
    }
    /**
     * D712: the RIDER asks what moving the pickup to `pickup` would mean - the market's governed eligibility (refusals, 409 `reasonCode`: `PICKUP_CHANGE_TOO_FAR` with
     * `params.maxDistanceMeters`, `PICKUP_CHANGE_LIMIT_REACHED`, `PICKUP_CHANGE_DRIVER_ARRIVED`, `PICKUP_CHANGE_ON_BOARD`, `PICKUP_CHANGE_PHASE_NOT_ALLOWED`,
     * `PICKUP_CHANGE_DISABLED`; the quote route's own for the new point) and the fare.
     */
    async quotePickupChange(rideId, pickup) {
        const res = await this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/pickup-change/quote`, body: { pickup }, auth: true });
        if (!res.ok)
            return res;
        const w = res.value;
        return {
            ok: true,
            value: {
                rideId: w.rideId,
                pickup: w.pickup,
                distanceFromAcceptedMeters: w.distanceFromAcceptedMeters,
                moveMeters: w.moveMeters,
                quoteId: w.quote.quoteId,
                ...(w.quote.expiresAt !== undefined ? { expiresAt: w.quote.expiresAt } : {}),
                fareReasonCode: w.fare.reasonCode,
                current: parseMoney(w.fare.current),
                total: parseMoney(w.fare.total),
                difference: { amountMinor: BigInt(w.fare.difference.amountMinor), currency: w.fare.difference.currency, direction: w.fare.difference.direction },
                policyVersion: w.policy.policyVersion,
                maxDistanceMeters: w.policy.maxDistanceMeters,
            },
        };
    }
    /** D712: the rider applies the move with the preview's quote ("Confirmar novo embarque"). Idempotent by `commandId`; the assigned driver is told. */
    async applyPickupChange(rideId, input) {
        const res = await this.client.request({
            method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/pickup-change`, body: input, auth: true,
        });
        if (!res.ok)
            return res;
        const w = res.value;
        return { ok: true, value: { rideId: w.rideId, phase: w.phase, status: w.status, version: w.version, pickup: w.pickup, fareReasonCode: w.fare.reasonCode, previousTotal: parseMoney(w.fare.previous), total: parseMoney(w.fare.total), policyVersion: w.policyVersion } };
    }
    /** D712: the assigned driver read the pickup the rider moved ("Entendi", `ack_pickup`). `409 NO_PICKUP_CHANGE_PENDING` when none waits. Idempotent by commandId. */
    acknowledgePickup(rideId, commandId) {
        return this.command(rideId, { commandId, type: 'ack_pickup' });
    }
    /** The rider's start PIN (rider-only; server-derived, never client-computed). */
    getPin(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/pin`, auth: true });
    }
    /** The ride's chat thread (participants only). */
    messages(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/messages`, auth: true });
    }
    /** Post a message; a refusal carries one of `RIDE_CHAT_REFUSAL_REASON_CODES` as `reasonCode`. `to: 'operator'` (D473) writes to the central. */
    sendMessage(rideId, body, opts = {}) {
        return this.client.request({ method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/messages`, body: { body, ...(opts.to !== undefined ? { to: opts.to } : {}) }, auth: true });
    }
    /** D473: send a ready message by its code (the server writes the governed text in `language`); `to: 'operator'` writes to the central. */
    sendQuickReply(rideId, code, opts = {}) {
        return this.client.request({
            method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/messages`, auth: true,
            body: { replyCode: code, ...(opts.language !== undefined ? { language: opts.language } : {}), ...(opts.to !== undefined ? { to: opts.to } : {}) },
        });
    }
    /** D473: the ready messages this participant is offered (empty when the market governs none). */
    quickReplies(rideId, language) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/quick-replies${language !== undefined ? `?language=${encodeURIComponent(language)}` : ''}`, auth: true });
    }
    /** D826: whether voice messages are on in the ride's market and their limits. */
    audioPolicy(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/messages/audio-policy`, auth: true });
    }
    /** D826: send a voice message (only while the ride is live; bounded by the market's governed `ride.chatAudio`). */
    sendAudio(rideId, upload) {
        return this.client.request({
            method: 'POST', path: `/v1/rides/${encodeURIComponent(rideId)}/messages/audio`, auth: true, timeoutMs: 60_000,
            body: { contentType: upload.contentType, bytesBase64: upload.bytesBase64, durationSeconds: upload.durationSeconds, ...(upload.to !== undefined ? { to: upload.to } : {}) },
        });
    }
    /** D826: where a native player reads one voice message (the ride's parties only), with the session's header; undefined without a session. */
    audioSource(rideId, messageId) {
        return this.client.authorizedSource(`/v1/rides/${encodeURIComponent(rideId)}/messages/${encodeURIComponent(messageId)}/audio`);
    }
    /** How to reach the other participant: chat only, no phone (D408 #4). */
    contact(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/rides/${encodeURIComponent(rideId)}/contact`, auth: true });
    }
}
//# sourceMappingURL=execution.js.map